import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import ShareIcon from '@mui/icons-material/Share'
import { Box, Menu, MenuItem } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import editIcon from '@/assets/images/EditIcon.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import { useDuplicateCase } from '@/hooks/useDuplicateCase'
import { useRemoveSharedCaseMutation } from '@/store/apiService'

import DeleteProject from './DeleteProject'
import EditProject from './EditProject'
import ShareProject from './ShareProject'

interface ActionMenuProps {
  anchorEl: null | HTMLElement
  handleMenuClose: () => void
  caseData: any
  refetch: () => void
  activeTab: number
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorEl,
  handleMenuClose,
  caseData,
  refetch,
  activeTab,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'project.projectActions',
  })

  const { t: translate } = useTranslation('translation', {
    keyPrefix: 'project.removeProjectDialog',
  })

  const { duplicate } = useDuplicateCase()
  const [removeSharedCase, { isLoading: isRemoving }] =
    useRemoveSharedCaseMutation()

  const caseId = caseData?.id

  const [editOpen, setEditOpen] = useState(false)
  const [selectedCase, setSelectedCase] = useState<any | null>(null)
  const [isDeleteDialog, setIsDeleteDialog] = useState(false)
  const [caseIdToDelete, setCaseIdToDelete] = useState<string | null>(null)
  const [isShareDialog, setIsShareDialog] = useState(false)
  const [isShareDeleteDialog, setIsShareDeleteDialog] = useState(false)

  const handleEdit = () => {
    setSelectedCase({
      id: caseId,
      patient_name: caseData?.name,
      description: caseData?.description,
      dentist: caseData?.dentist,
    })
    setEditOpen(true)
  }

  const handleShare = () => {
    setSelectedCase({
      id: caseId,
      patient_name: caseData?.name,
    })
    setIsShareDialog(true)
  }

  const onDuplicateMenuItemClick = async () => {
    handleMenuClose()
    if (!caseId) return
    await duplicate({ ids: [caseId], refetch })
  }
  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
              color: 'white',
              mt: 1,
              boxShadow:
                '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
              borderRadius: '4px',
            },
          },
          list: {
            sx: {
              bgcolor: 'rgba(41, 41, 41, 1)',
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {activeTab === 0 ? (
          <>
            <MenuItem
              onClick={() => {
                handleMenuClose()
                handleShare()
              }}
              sx={{ '&:hover': { backgroundColor: '#33393E' } }}
            >
              <ShareIcon sx={{ mr: '12px' }} />
              {t('share')}
            </MenuItem>

            <MenuItem
              onClick={onDuplicateMenuItemClick}
              sx={{ '&:hover': { backgroundColor: '#33393E' } }}
            >
              <ContentCopyIcon sx={{ mr: '12px' }} />
              {t('duplicate')}
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose()
                handleEdit()
              }}
              sx={{ '&:hover': { backgroundColor: '#33393E' } }}
            >
              <Box
                component="img"
                src={editIcon}
                alt="Edit"
                sx={{ width: 24, height: 24, mr: '12px' }}
              />
              {t('edit')}
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose()
                setCaseIdToDelete(caseId)
                setIsDeleteDialog(true)
              }}
              sx={{ '&:hover': { backgroundColor: '#33393E' } }}
            >
              <DeleteIcon sx={{ mr: '12px' }} />
              {t('delete')}
            </MenuItem>
          </>
        ) : (
          <MenuItem
            onClick={() => {
              handleMenuClose()
              setCaseIdToDelete(caseId)
              setIsShareDeleteDialog(true)
            }}
            sx={{ '&:hover': { backgroundColor: '#33393E' } }}
          >
            <DeleteIcon sx={{ mr: '12px' }} />
            {t('remove')}
          </MenuItem>
        )}
      </Menu>

      {selectedCase && (
        <EditProject
          open={editOpen}
          onClose={() => setEditOpen(false)}
          initialData={selectedCase}
          refetchRecipe={refetch}
        />
      )}

      {isShareDialog && (
        <ShareProject
          open={isShareDialog}
          onClose={() => setIsShareDialog(false)}
          initialData={selectedCase}
          refetchRecipe={refetch}
        />
      )}

      <DeleteProject
        isOpen={isDeleteDialog}
        onClose={() => {
          setIsDeleteDialog(false)
          setCaseIdToDelete(null)
        }}
        caseId={caseIdToDelete}
        refetch={refetch}
      />

      <Dialog
        triggerElement={null}
        isOpen={isShareDeleteDialog}
        onClose={() => setIsShareDeleteDialog(false)}
        title={translate('title')}
        content={<p>{translate('description')}</p>}
        paperStyles={{ width: '600px' }}
        actionButtons={
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              label={translate('cancelButton')}
              onClick={() => {
                setIsShareDeleteDialog(false)
                handleMenuClose()
              }}
            />
            <Button
              variant="contained"
              label={translate('removeButton')}
              onClick={async () => {
                if (caseIdToDelete) {
                  try {
                    await removeSharedCase({
                      caseId: caseIdToDelete,
                    }).unwrap()
                    setIsShareDeleteDialog(false)
                    toast.success(translate('removeSuccess'))
                    refetch()
                  } catch (err) {
                    toast.error(translate('removeFailed'))
                  }
                }
              }}
              loading={isRemoving}
              disabled={isRemoving}
            />
          </Box>
        }
      />
    </>
  )
}

export default ActionMenu
